import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router';
import {
    Plus,
    Edit01,
    Trash01,
    SearchLg,
    FileCheck02,
    FileCode01
} from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, DialogTrigger, Dialog, ModalOverlay } from '@/components/application/modals/modal';
import { Dropdown } from '@/components/base/dropdown/dropdown';
import { documentSchemaService, parserRuleService, DOCUMENT_TYPES } from '@/data/mock-document-schemas';
import type { DocumentSchema, CreateDocumentSchemaData, DocumentSchemaFilters, DocumentType } from '@/types/document-schema';
import { useTranslation } from 'react-i18next';

export const DocumentTypeSchemasPage = () => {
    const { documentType } = useParams<{ documentType: DocumentType }>();
    const navigate = useNavigate();
    const { t } = useTranslation(['pages:documents-admin', 'common']);
    const [schemas, setSchemas] = useState<DocumentSchema[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [regionFilter, setRegionFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState<boolean | ''>('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSchema, setEditingSchema] = useState<DocumentSchema | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [schemaToDelete, setSchemaToDelete] = useState<string | null>(null);

    const currentDocType = DOCUMENT_TYPES.find((dt) => dt.key === documentType);

    const [formData, setFormData] = useState<CreateDocumentSchemaData>({
        document_type: documentType || 'bill_of_lading',
        schema_name: '',
        schema_code: '',
        version: '1.0',
        description: '',
        use_cases: '',
        region: '',
        country_codes: [],
        carrier_codes: [],
        is_default: false,
        is_active: true,
    });

    const regionOptions = [
        { id: '', label: t('schemas.regionPlaceholder') },
        { id: 'asia', label: 'Asia' },
        { id: 'europe', label: 'Europa' },
        { id: 'americas', label: 'Americhe' },
        { id: 'africa', label: 'Africa' },
        { id: 'middle_east', label: 'Medio Oriente' },
    ];

    const statusOptions = [
        { id: '', label: t('schemas.statusPlaceholder') },
        { id: 'true', label: t('schemas.status.active') },
        { id: 'false', label: t('schemas.status.inactive') },
    ];

    useEffect(() => {
        if (documentType) {
            loadSchemas();
        }
    }, [documentType, searchTerm, regionFilter, statusFilter]);

    const loadSchemas = () => {
        if (!documentType) return;
        setIsLoading(true);
        const filters: DocumentSchemaFilters = {
            document_type: documentType,
            search: searchTerm || undefined,
            region: regionFilter || undefined,
            is_active: statusFilter !== '' ? statusFilter === true : undefined,
        };
        const filtered = documentSchemaService.search(filters);
        setSchemas(filtered);
        setIsLoading(false);
    };

    const handleOpenModal = (schema?: DocumentSchema) => {
        if (schema) {
            setEditingSchema(schema);
            setFormData({
                document_type: schema.document_type,
                schema_name: schema.schema_name,
                schema_code: schema.schema_code,
                version: schema.version,
                description: schema.description,
                use_cases: schema.use_cases,
                region: schema.region,
                country_codes: schema.country_codes,
                carrier_codes: schema.carrier_codes,
                is_default: schema.is_default,
                is_active: schema.is_active,
            });
        } else {
            setEditingSchema(null);
            setFormData({
                document_type: documentType || 'bill_of_lading',
                schema_name: '',
                schema_code: '',
                version: '1.0',
                description: '',
                use_cases: '',
                region: '',
                country_codes: [],
                carrier_codes: [],
                is_default: false,
                is_active: true,
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingSchema(null);
    };

    const handleSave = () => {
        if (editingSchema) {
            documentSchemaService.update(editingSchema.document_schema_id, {
                ...formData,
                document_schema_id: editingSchema.document_schema_id,
            });
        } else {
            if (!formData.schema_code && formData.schema_name) {
                formData.schema_code = formData.schema_name
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '_')
                    .replace(/^_+|_+$/g, '');
            }
            documentSchemaService.create(formData);
        }
        loadSchemas();
        handleCloseModal();
    };

    const handleDeleteClick = (id: string) => {
        setSchemaToDelete(id);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (schemaToDelete) {
            documentSchemaService.delete(schemaToDelete);
            loadSchemas();
            setIsDeleteModalOpen(false);
            setSchemaToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setIsDeleteModalOpen(false);
        setSchemaToDelete(null);
    };

    const getRulesCount = (schemaId: string): number => {
        return parserRuleService.getBySchemaId(schemaId).length;
    };

    const generateSchemaCode = (name: string) => {
        const code = name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '');
        setFormData({ ...formData, schema_code: code });
    };

    if (isLoading || !documentType) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm">
                <Link
                    to="/admin/documents/types"
                    className="text-tertiary hover:text-primary"
                >
                    {t('breadcrumb.documentTypes')}
                </Link>
                <span className="text-tertiary">/</span>
                <span className="text-primary">{currentDocType?.label || documentType}</span>
                <span className="text-tertiary">/</span>
                <span className="text-primary">{t('breadcrumb.schemas')}</span>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-primary">
                        {t('schemas.title')} - {currentDocType?.label || documentType}
                    </h2>
                    <p className="text-tertiary mt-1">{t('schemas.description')}</p>
                </div>
                <Button
                    onClick={() => handleOpenModal()}
                    size="md"
                    color="primary"
                    iconLeading={Plus}
                >
                    {t('schemas.newSchema')}
                </Button>
            </div>

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Input
                        placeholder={t('schemas.searchPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select
                        placeholder={t('schemas.regionPlaceholder')}
                        items={regionOptions}
                        selectedKey={regionFilter}
                        onSelectionChange={(key) => setRegionFilter(key as string)}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('schemas.statusPlaceholder')}
                        items={statusOptions}
                        selectedKey={statusFilter !== '' ? String(statusFilter) : ''}
                        onSelectionChange={(key) => setStatusFilter(key === '' ? '' : key === 'true')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Schemas Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={`${t('schemas.title')} (${schemas.length})`}
                    description={t('schemas.description')}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('schemas.headers.schemaName')} isRowHeader />
                        <Table.Head label={t('schemas.headers.schemaCode')} />
                        <Table.Head label={t('schemas.headers.region')} />
                        <Table.Head label={t('schemas.headers.attributeCount')} />
                        <Table.Head label={t('schemas.headers.parserRulesCount')} />
                        <Table.Head label={t('schemas.headers.isDefault')} />
                        <Table.Head label={t('schemas.headers.isActive')} />
                        <Table.Head label={t('schemas.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {schemas.map((schema) => (
                            <Table.Row key={schema.document_schema_id}>
                                <Table.Cell>
                                    <div className="font-medium text-primary">{schema.schema_name}</div>
                                    {schema.description && (
                                        <div className="text-sm text-tertiary">{schema.description}</div>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary font-mono">{schema.schema_code}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{schema.region || '-'}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{schema.attribute_count}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{getRulesCount(schema.document_schema_id)}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    {schema.is_default ? (
                                        <Badge color="brand" size="sm">{t('schemas.status.default')}</Badge>
                                    ) : (
                                        <span className="text-sm text-tertiary">-</span>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    {schema.is_active ? (
                                        <Badge color="success" size="sm">{t('schemas.status.active')}</Badge>
                                    ) : (
                                        <Badge color="error" size="sm">{t('schemas.status.inactive')}</Badge>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    <Dropdown.Root>
                                        <Dropdown.DotsButton />
                                        <Dropdown.Popover className="w-min">
                                            <Dropdown.Menu>
                                                <Dropdown.Item
                                                    id="viewAttributes"
                                                    icon={FileCheck02}
                                                    label={t('schemas.actions.viewAttributes')}
                                                    onAction={() => {
                                                        navigate(`/admin/documents/types/${documentType}/schemas/${schema.document_schema_id}/attributes`);
                                                    }}
                                                />
                                                <Dropdown.Item
                                                    id="parserRules"
                                                    icon={FileCode01}
                                                    label={t('schemas.actions.parserRules')}
                                                    onAction={() => {
                                                        navigate(`/admin/documents/types/${documentType}/schemas/${schema.document_schema_id}/parser-rules`);
                                                    }}
                                                />
                                                <Dropdown.Item
                                                    id="edit"
                                                    icon={Edit01}
                                                    label={t('schemas.actions.edit')}
                                                    onAction={() => handleOpenModal(schema)}
                                                />
                                                <Dropdown.Separator />
                                                <Dropdown.Item
                                                    id="delete"
                                                    icon={Trash01}
                                                    label={t('schemas.actions.delete')}
                                                    onAction={() => handleDeleteClick(schema.document_schema_id)}
                                                />
                                            </Dropdown.Menu>
                                        </Dropdown.Popover>
                                    </Dropdown.Root>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {schemas.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <h3 className="text-lg font-medium text-primary mb-2">{t('noData.title')}</h3>
                        <p className="text-tertiary">{t('noData.message')}</p>
                    </div>
                </div>
            )}

            {/* Modal */}
            <DialogTrigger isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-4xl rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-6">
                                    {editingSchema ? t('schemas.modal.edit') : t('schemas.modal.new')}
                                </h2>

                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div>
                                            <label className="text-sm font-medium text-primary mb-1 block">
                                                {t('schemas.modal.fields.documentType')}
                                            </label>
                                            <div className="text-primary">{currentDocType?.label || documentType}</div>
                                        </div>

                                        <Input
                                            label={t('schemas.modal.fields.version')}
                                            placeholder={t('schemas.modal.fields.versionPlaceholder')}
                                            value={formData.version}
                                            onChange={(value) => setFormData({ ...formData, version: value })}
                                            size="md"
                                        />
                                    </div>

                                    <Input
                                        label={t('schemas.modal.fields.schemaName')}
                                        placeholder={t('schemas.modal.fields.schemaNamePlaceholder')}
                                        value={formData.schema_name}
                                        onChange={(value) => {
                                            setFormData({ ...formData, schema_name: value });
                                            if (!editingSchema) {
                                                generateSchemaCode(value);
                                            }
                                        }}
                                        isRequired
                                        size="md"
                                    />

                                    <Input
                                        label={t('schemas.modal.fields.schemaCode')}
                                        placeholder={t('schemas.modal.fields.schemaCodePlaceholder')}
                                        value={formData.schema_code}
                                        onChange={(value) => setFormData({ ...formData, schema_code: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <Input
                                        label={t('schemas.modal.fields.description')}
                                        placeholder={t('schemas.modal.fields.descriptionPlaceholder')}
                                        value={formData.description || ''}
                                        onChange={(value) => setFormData({ ...formData, description: value })}
                                        size="md"
                                    />

                                    <Input
                                        label={t('schemas.modal.fields.useCases')}
                                        placeholder={t('schemas.modal.fields.useCasesPlaceholder')}
                                        value={formData.use_cases || ''}
                                        onChange={(value) => setFormData({ ...formData, use_cases: value })}
                                        size="md"
                                    />

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <Select
                                            label={t('schemas.modal.fields.region')}
                                            items={regionOptions.filter((opt) => opt.id !== '')}
                                            selectedKey={formData.region || ''}
                                            onSelectionChange={(key) => setFormData({ ...formData, region: key as string })}
                                            size="md"
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select>

                                        <Input
                                            label={t('schemas.modal.fields.countryCodes')}
                                            placeholder={t('schemas.modal.fields.countryCodesPlaceholder')}
                                            value={formData.country_codes?.join(', ') || ''}
                                            onChange={(value) =>
                                                setFormData({
                                                    ...formData,
                                                    country_codes: value.split(',').map((c) => c.trim()).filter(Boolean),
                                                })
                                            }
                                            size="md"
                                        />
                                    </div>

                                    <Input
                                        label={t('schemas.modal.fields.carrierCodes')}
                                        placeholder={t('schemas.modal.fields.carrierCodesPlaceholder')}
                                        value={formData.carrier_codes?.join(', ') || ''}
                                        onChange={(value) =>
                                            setFormData({
                                                ...formData,
                                                carrier_codes: value.split(',').map((c) => c.trim()).filter(Boolean),
                                            })
                                        }
                                        size="md"
                                    />

                                    <div className="flex items-center gap-4">
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={formData.is_default || false}
                                                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                                                className="rounded border-secondary"
                                            />
                                            <span className="text-sm text-primary">{t('schemas.modal.fields.isDefault')}</span>
                                        </label>
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={formData.is_active !== undefined ? formData.is_active : true}
                                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                                className="rounded border-secondary"
                                            />
                                            <span className="text-sm text-primary">{t('schemas.modal.fields.isActive')}</span>
                                        </label>
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-4 mt-6">
                                    <Button onClick={handleCloseModal} size="md" color="secondary">
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button onClick={handleSave} size="md" color="primary">
                                        {editingSchema ? t('modal.save') : t('modal.create')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>

            {/* Delete Confirmation Modal */}
            <DialogTrigger isOpen={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <h2 className="text-xl font-semibold text-primary mb-4">
                                    {t('modal.confirmDelete')}
                                </h2>
                                <p className="text-tertiary mb-6">
                                    {t('modal.confirmDeleteMessage')}
                                </p>
                                <div className="flex items-center justify-end gap-4">
                                    <Button onClick={handleDeleteCancel} size="md" color="secondary">
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button onClick={handleDeleteConfirm} size="md" color="primary-destructive">
                                        {t('modal.delete')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </div>
    );
};
